/**
 * React Hook for Navigation Preferences
 *
 * Manages user-specific workspace navigation preferences.
 * Features: favorites, hidden tools, custom sort order.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import {
  getNavigationPreferences,
  toggleFavorite,
  toggleVisibility,
  updateSortOrder,
  resetNavigationPreferences,
  getWorkspaceTools,
  type WorkspaceTool,
} from '@/lib/api/navigation';
import { DEFAULT_NAVIGATION_PREFERENCES } from '@/types/navigation';
import type { NavigationPreferences } from '@/types/navigation';
import * as Icons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { DEFAULT_PRIMARY_TOOL_IDS } from '@/constants/sidebarDefaults';

const QUERY_KEY = ['navigationPreferences'];
const TOOLS_QUERY_KEY = ['workspaceTools'];

// Workspace Tool with Lucide Icon Component
export interface WorkspaceToolWithIcon extends Omit<WorkspaceTool, 'icon'> {
  icon: React.ComponentType<{ className?: string }>;
}

export function useNavigationPreferences() {
  const queryClient = useQueryClient();

  // Fetch preferences
  const { data, isLoading: isLoadingPreferences, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getNavigationPreferences,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: DEFAULT_NAVIGATION_PREFERENCES,
  });

  const preferences = data ?? DEFAULT_NAVIGATION_PREFERENCES;

  // Fetch workspace tools from API
  const { data: toolsData, isLoading: isLoadingTools } = useQuery({
    queryKey: TOOLS_QUERY_KEY,
    queryFn: getWorkspaceTools,
    staleTime: 5 * 60 * 1000, // 5 minutes (tools change rarely)
  });

  // Convert icon names to Lucide components
  const workspaceTools: WorkspaceToolWithIcon[] = useMemo(() => {
    if (!toolsData) return [];
    return toolsData.map((tool) => ({
      ...tool,
      icon: (Icons[tool.icon as keyof typeof Icons] as LucideIcon) || Icons.HelpCircle,
    }));
  }, [toolsData]);

  const isLoading = isLoadingPreferences || isLoadingTools;

  // Toggle favorite mutation
  const favoriteMutation = useMutation({
    mutationFn: ({ toolId, isFavorite }: { toolId: string; isFavorite: boolean }) =>
      toggleFavorite(toolId, isFavorite),
    onMutate: async ({ toolId, isFavorite }) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<NavigationPreferences>(QUERY_KEY);
      queryClient.setQueryData<NavigationPreferences>(QUERY_KEY, (old) => {
        if (!old) return old;
        const favorites = isFavorite
          ? [...old.favorites, toolId]
          : old.favorites.filter((id) => id !== toolId);
        const hidden = old.hidden.filter((id) => id !== toolId);
        return { ...old, favorites, hidden };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  // Toggle visibility mutation
  const visibilityMutation = useMutation({
    mutationFn: ({ toolId, isHidden }: { toolId: string; isHidden: boolean }) =>
      toggleVisibility(toolId, isHidden),
    onMutate: async ({ toolId, isHidden }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<NavigationPreferences>(QUERY_KEY);
      queryClient.setQueryData<NavigationPreferences>(QUERY_KEY, (old) => {
        if (!old) return old;
        const hidden = isHidden
          ? [...old.hidden, toolId]
          : old.hidden.filter((id) => id !== toolId);
        const favorites = old.favorites.filter((id) => id !== toolId);
        return { ...old, hidden, favorites };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  // Sort order mutation (debounced to avoid excessive API calls during drag)
  const sortOrderMutation = useMutation({
    mutationFn: (sortOrder: Record<string, number>) => updateSortOrder(sortOrder),
    onMutate: async (sortOrder) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<NavigationPreferences>(QUERY_KEY);
      queryClient.setQueryData<NavigationPreferences>(QUERY_KEY, (old) => {
        if (!old) return old;
        return { ...old, sortOrder };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  // Reset mutation
  const resetMutation = useMutation({
    mutationFn: resetNavigationPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  // Helper to sort tools by preferences
  const sortTools = useCallback(
    (tools: WorkspaceToolWithIcon[]): WorkspaceToolWithIcon[] => {
      return [...tools].sort((a, b) => {
        // Priority 1: User custom sort order (from preferences)
        // Priority 2: Computed sort order (auto-positioning for new tools)
        // Priority 3: Original sort order (database default)
        const orderA = preferences.sortOrder[a.id] ?? a.computed_sort_order ?? a.sort_order ?? 999;
        const orderB = preferences.sortOrder[b.id] ?? b.computed_sort_order ?? b.sort_order ?? 999;
        return orderA - orderB;
      });
    },
    [preferences.sortOrder]
  );

  // Get organized tools
  const organizedTools = useMemo(() => {
    const favoriteTools: WorkspaceToolWithIcon[] = [];
    const normalTools: WorkspaceToolWithIcon[] = [];
    const hiddenTools: WorkspaceToolWithIcon[] = [];

    for (const tool of workspaceTools) {
      if (preferences.favorites.includes(tool.id)) {
        favoriteTools.push(tool);
      } else if (preferences.hidden.includes(tool.id)) {
        hiddenTools.push(tool);
      } else {
        normalTools.push(tool);
      }
    }

    const sortedFavorites = sortTools(favoriteTools);
    const sortedNormal = sortTools(normalTools);
    const sortedHidden = sortTools(hiddenTools);

    // Primary = favorites if any, else the 4 hardcoded defaults
    // (filtered to existing + non-hidden tools, in declaration order)
    let primary: WorkspaceToolWithIcon[];
    let collapsed: WorkspaceToolWithIcon[];

    if (sortedFavorites.length > 0) {
      primary = sortedFavorites;
      collapsed = sortedNormal;
    } else {
      const byId = new Map(workspaceTools.map((t) => [t.id, t]));
      primary = DEFAULT_PRIMARY_TOOL_IDS
        .map((id) => byId.get(id))
        .filter((t): t is WorkspaceToolWithIcon =>
          Boolean(t) && !preferences.hidden.includes(t!.id)
        );
      const primaryIdSet = new Set(primary.map((t) => t.id));
      collapsed = sortedNormal.filter((t) => !primaryIdSet.has(t.id));
    }

    return {
      primary,
      collapsed,
      favorites: sortedFavorites,
      normal: sortedNormal,
      hidden: sortedHidden,
      all: workspaceTools,
    };
  }, [workspaceTools, preferences.favorites, preferences.hidden, sortTools]);

  // Check if tool is favorite
  const isFavorite = useCallback(
    (toolId: string) => preferences.favorites.includes(toolId),
    [preferences.favorites]
  );

  // Check if tool is hidden
  const isHidden = useCallback(
    (toolId: string) => preferences.hidden.includes(toolId),
    [preferences.hidden]
  );

  return {
    // State
    preferences,
    isLoading,
    error,
    organizedTools,

    // Helpers
    isFavorite,
    isHidden,

    // Actions
    toggleFavorite: (toolId: string, isFavorite: boolean) =>
      favoriteMutation.mutate({ toolId, isFavorite }),
    toggleVisibility: (toolId: string, isHidden: boolean) =>
      visibilityMutation.mutate({ toolId, isHidden }),
    updateSortOrder: sortOrderMutation.mutate,
    reset: resetMutation.mutate,

    // Mutation states
    isUpdating:
      favoriteMutation.isPending ||
      visibilityMutation.isPending ||
      sortOrderMutation.isPending ||
      resetMutation.isPending,
  };
}
